import { Button, Icon, Header, Segment, Transition } from "semantic-ui-react";
import React, { Component } from "react";
import { Input, Dropdown, Form, Modal, Container } from 'semantic-ui-react'
import Countdown from 'react-countdown';
import { isMobile } from 'react-device-detect';
import { captureEvent } from './utils/posthog';
import { guestPromptOpenSource, trackGuestLimitShown } from './guestLimit';

// Add CSS styles for ripple effect
const rainbowStyles = `
  @keyframes ripple {
    0% {
      transform: scale(1);
      opacity: 1;
    }
    100% {
      transform: scale(2.5);
      opacity: 0;
    }
  }
  
  @keyframes button-pulse {
    0%, 100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  @keyframes mobile-shake {
    0%, 100% { transform: translateX(-50%); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(calc(-50% - 10px)); }
    20%, 40%, 60%, 80% { transform: translateX(calc(-50% + 10px)); }
  }

  .mobile-shaking {
    animation: mobile-shake 1s ease-in-out !important;
  }
  
  @keyframes desktop-shake {
    0%, 100% { transform: translateX(0); }
    10%, 30%, 50%, 70%, 90% { transform: translateX(-10px); }
    20%, 40%, 60%, 80% { transform: translateX(10px); }
  }

  .desktop-shaking {
    animation: desktop-shake 1s ease-in-out !important;
  }
  
  @keyframes shimmer {
    0% {
      left: -200px;
    }
    100% {
      left: calc(100% + 200px);
    }
  }
  
  #action-message-button {
    position: relative !important;
    overflow: hidden !important;
    width: 300px !important;
    min-width: 280px !important;
    max-width: calc(100vw - 40px) !important;
  }

  @media (max-width: 768px) {
    #action-message-button {
      width: calc(100vw - 60px) !important;
      min-width: unset !important;
      max-width: 280px !important;
    }
  }
  
  #action-message-button:hover {
    animation: button-pulse 1s ease-in-out infinite !important;
    box-shadow: 0 0 20px rgba(75, 184, 19, 0.6) !important;
    transition: all 0.3s ease !important;
  }
  
  #action-message-button:hover::before {
    content: '' !important;
    position: absolute !important;
    top: 50% !important;
    left: 50% !important;
    width: 100% !important;
    height: 100% !important;
    background: radial-gradient(circle, rgba(75, 184, 19, 0.3) 0%, rgba(75, 184, 19, 0.1) 50%, transparent 70%) !important;
    border-radius: 50% !important;
    transform: translate(-50%, -50%) scale(1) !important;
    animation: ripple 2s ease-out infinite !important;
    pointer-events: none !important;
    z-index: -1 !important;
    will-change: transform, opacity !important;
    contain: layout !important;
  }
  
  #action-message-button.rainbow-button:hover::before {
    background: radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0.2) 50%, transparent 70%) !important;
  }
  
  #action-message-button.shimmer-effect::after {
    content: '' !important;
    position: absolute !important;
    top: 0 !important;
    left: -200px !important;
    width: 200px !important;
    height: 100% !important;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent) !important;
    animation: shimmer 1.5s ease-in-out !important;
    pointer-events: none !important;
    z-index: 1 !important;
  }
  
  #action-message-button.shimmer-effect.rainbow-button::after {
    content: '' !important;
    position: absolute !important;
    top: 0 !important;
    left: -200px !important;
    width: 200px !important;
    height: 100% !important;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent) !important;
    animation: shimmer 1.5s ease-in-out !important;
    pointer-events: none !important;
    z-index: 1 !important;
  }

  .action-message-close-button {
    color: var(--card-foreground) !important;
    background: transparent !important;
    border: none !important;
    transition: all 0.2s ease !important;
    border-radius: var(--radius) !important;
    opacity: 0.8;
    z-index: 1001;
  }

  .action-message-close-button:hover {
    background: var(--secondary) !important;
    color: var(--primary) !important;
    opacity: 1;
    transform: scale(1.1);
  }
  /* Secondary CTA link: Semantic's .ui.basic.button forces its own color with !important */
  #action-message-secondary-button.ui.button {
    background: transparent !important;
    box-shadow: none !important;
    border: none !important;
    color: var(--card-foreground) !important;
    text-decoration: underline !important;
    font-weight: normal !important;
    font-size: 0.8rem !important;
    padding: 2px 4px !important;
    margin: 6px 0 0 0 !important;
    max-width: calc(100vw - 40px) !important;
    white-space: normal !important;
    line-height: 1.3 !important;
    text-align: center !important;
  }
  #action-message-secondary-button.ui.button.on-canvas {
    font-weight: bold !important;
    text-shadow: 0px 0px 2px var(--card) !important;
  }
`;

// Inject styles into document head
if (typeof document !== 'undefined') {
    // Check if styles are already injected
    if (!document.getElementById('rainbow-border-styles')) {
        const styleSheet = document.createElement("style");
        styleSheet.id = 'rainbow-border-styles';
        styleSheet.type = "text/css";
        styleSheet.innerText = rainbowStyles;
        document.head.appendChild(styleSheet);
        console.log('Rainbow border hover styles injected');
    }
}


// Renderer callback with condition
const format = (n) => {
    return n > 9 ? n : `0${n}`;
}
const renderer = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
        // Render a completed state
        return <div />;
    } else {
        // Render a countdown
        return (
            <>
                <div style={{ marginBottom: 3 }}>
                    <span style={{ color: 'dodgerblue', fontWeight: 'bold' }}>%50</span> discount coupon!
                </div>
                <div style={{ marginBottom: 3 }}>
                    Coupon:
                </div>
                <div style={{ marginBottom: 3 }}>
                    <Header as='h3' style={{ color: '#f3701d' }}>
                        QQEMA
                    </Header>
                </div>
                <div style={{ marginBottom: 3 }}>
                    Offer ends at:
                </div>
                <div style={{ marginBottom: 0 }}>

                    <Header as='h3' style={{ color: '#f3701d' }}>
                        {format(days)} : {format(hours)} : {format(minutes)} : {format(seconds)}
                    </Header>

                </div>
                <div>
                    <h6 style={{ marginTop: 0 }}>
                        <span style={{ paddingLeft: 2 }}>DYS</span>
                        <span style={{ paddingLeft: 15 }}>HRS</span>
                        <span style={{ paddingLeft: 14 }}>MIN</span>
                        <span style={{ paddingLeft: 15 }}>SEC</span>
                    </h6>
                </div>
            </>

        )
    }
};

const LOCKED_FEATURE_TRIGGER_COUNT_KEY = 'lockedFeatureTriggerCount';
const PRICE_CTA_MIN_TRIGGERS = 3;

export default class ActionMessage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isVisible: true,
            isShaking: false,
            rainbowFrame: 0,
            isShimmering: false,
            // Guests on the empty lab (/lab) see the sign-up prompt on load; guests viewing someone else's saved circuit do not
            initialGuestShow: !this.props.user && !this.props.looking_at_others_circuit,
            hasReceivedLimitation: false,
            triggerCount: this.readTriggerCount()
        };
        this.rainbowInterval = null;
        this.shimmerInterval = null;
        this.rainbowColors = [
            '#ff0000', '#ff4000', '#ff8000', '#ffbf00', '#ffff00',
            '#bfff00', '#80ff00', '#40ff00', '#00ff00', '#00ff40',
            '#00ff80', '#00ffbf', '#00ffff', '#00bfff', '#0080ff',
            '#0040ff', '#0000ff', '#4000ff', '#8000ff', '#bf00ff',
            '#ff00ff', '#ff00bf', '#ff0080', '#ff0040'
        ];
    }

    // One count per signed-in user; guests are not counted
    triggerCountKey() {
        const userId = this.props.user?.id;
        return userId ? `${LOCKED_FEATURE_TRIGGER_COUNT_KEY}:${userId}` : null;
    }

    readTriggerCount() {
        const key = this.triggerCountKey();
        if (!key) return 0;
        try {
            const stored = parseInt(window.localStorage.getItem(key), 10);
            return Number.isFinite(stored) && stored > 0 ? stored : 0;
        } catch (e) {
            return 0;
        }
    }

    writeTriggerCount(count) {
        const key = this.triggerCountKey();
        if (!key) return;
        try {
            window.localStorage.setItem(key, String(count));
        } catch (e) {
            // localStorage unavailable: the count lives in memory only
        }
    }

    handleLockedFeatureTrigger() {
        if (!this.triggerCountKey()) return;
        // Re-read storage so triggers from other tabs/remounts are counted too
        const triggerCount = Math.max(this.state.triggerCount, this.readTriggerCount()) + 1;
        this.writeTriggerCount(triggerCount);
        this.setState({ triggerCount });
    }

    getPriceCtaText(t) {
        const { min_plan_price } = this.props;
        if (!min_plan_price || this.state.triggerCount < PRICE_CTA_MIN_TRIGGERS) return null;
        return t.startNowForOnly.replace('{price}', min_plan_price);
    }

    // One source of truth for both render paths: what the button says, its subtext, where it goes,
    // and an optional quieter secondary link under it.
    // A contextual admin CTA wins over the segment matrix; everyone else is routed by user.segment.
    getCtaForUser(t) {
        const { user, segment, cta, cheapest_plan, current_locale, l } = this.props;
        const upgradeText = this.getPriceCtaText(t)
            || (cheapest_plan ? t.upgradeNow.replace('{cheapest_plan}', cheapest_plan) : 'Upgrade Now');
        const seePlans = { action: 'see_plans', text: t.seePlans, href: '/pricing-table' };

        if (cta) {
            return { variant: 'contextual', action: 'contextual', text: cta.button_text || upgradeText, href: cta.url || '/pricing-table' };
        }
        if (!user) {
            // Locales without an inline table keep the server-localized text they had before
            const hasInlineCopy = !!t.__hasInlineCopy;
            return {
                variant: 'anonymous',
                action: 'sign_up',
                text: hasInlineCopy ? t.tryFree : ((l && l.sign_up_now) || t.tryFree),
                subtext: (l && l.no_credit_card_required) || t.noCreditCardRequired,
                href: '/users/sign_up'
            };
        }

        const requestAccessPath = `/${current_locale || 'en'}/request-access`;
        if (segment === 'teacher') {
            // No license, no staff role, not a student account (User#teacher_demo_eligible?): the free classroom demo
            if (user.demo_eligible) {
                return { variant: 'teacher_demo', action: 'start_teaching', text: t.startFreeClassroom, href: `/${current_locale || 'en'}/start-teaching`, secondary: seePlans };
            }
            return { variant: 'teacher', action: 'request_access', text: t.requestClassroomLicense, href: requestAccessPath, secondary: seePlans };
        }
        if (segment === 'business') {
            return { variant: 'business', action: 'request_access', text: t.contactTeamAccess, href: requestAccessPath };
        }
        if (segment === 'hobbyist') {
            return { variant: 'hobbyist', action: 'upgrade', text: upgradeText, href: '/pricing-table' };
        }
        // Students, and users who never picked a segment: they are most of the buyers, so the upgrade leads;
        // getting it free through their teacher is the quieter second option
        return {
            variant: 'student',
            action: 'upgrade',
            text: upgradeText,
            href: '/pricing-table',
            secondary: { action: 'invite_teacher', text: t.askTeacherSecondary, onClick: this.props.onOpenInviteTeacherModal }
        };
    }

    trackCtaClick(ctaInfo, target) {
        const segment = this.props.segment || 'unknown';
        captureEvent('lab_cta_clicked', {
            segment,
            cta_variant: ctaInfo.variant,
            cta_action: target.action,
            cta_text: target.text,
            user_signed_in: !!this.props.user
        });
        if (typeof window.gtag === 'function') {
            window.gtag('event', 'lab_cta_clicked', { cta_variant: ctaInfo.variant, cta_action: target.action, segment });
        }
    }

    // Opens a CTA target: an onClick (e.g. the invite modal) wins over its href
    openCtaTarget(target) {
        if (target.onClick) {
            target.onClick();
            return;
        }
        window.open(target.href, '_blank');
    }

    handleCtaClick(t) {
        const ctaInfo = this.getCtaForUser(t);
        this.trackCtaClick(ctaInfo, ctaInfo);
        if (ctaInfo.variant === 'teacher') {
            captureEvent('teacher_request_quote_clicked', { source: 'action_message' });
        }
        if (ctaInfo.variant === 'teacher_demo') {
            captureEvent('teacher_demo_cta_clicked', { source: 'action_message' });
            if (typeof window.gtag === 'function') {
                window.gtag('event', 'teacher_demo_cta_clicked', { source: 'action_message' });
            }
        }
        this.openCtaTarget(ctaInfo);
    }

    handleSecondaryCtaClick(e, t) {
        e.preventDefault();
        e.stopPropagation();
        const ctaInfo = this.getCtaForUser(t);
        if (!ctaInfo.secondary) return;
        this.trackCtaClick(ctaInfo, ctaInfo.secondary);
        this.openCtaTarget(ctaInfo.secondary);
    }

    renderSecondaryCta(ctaInfo, t, onCanvas) {
        if (!ctaInfo.secondary) return null;
        return (
            <Button
                id="action-message-secondary-button"
                className={onCanvas ? 'on-canvas' : ''}
                type="button"
                dir="auto"
                basic
                compact
                aria-label={ctaInfo.secondary.text}
                onClick={(e) => this.handleSecondaryCtaClick(e, t)}
                content={ctaInfo.secondary.text}
            />
        );
    }

    startRainbowAnimation() {
        if (this.rainbowInterval) {
            clearInterval(this.rainbowInterval);
        }

        this.rainbowInterval = setInterval(() => {
            this.setState(prevState => ({
                rainbowFrame: (prevState.rainbowFrame + 1) % this.rainbowColors.length
            }));
        }, 80); // Change color every 80ms for smoother, calmer animation
    }

    stopRainbowAnimation() {
        if (this.rainbowInterval) {
            clearInterval(this.rainbowInterval);
            this.rainbowInterval = null;
        }
        this.setState({ rainbowFrame: 0 });
    }


    startShimmerInterval() {
        // Start shimmer effect every 10 seconds
        this.shimmerInterval = setInterval(() => {
            if (!this.state.isShaking) { // Only shimmer if not currently shaking
                this.setState({ isShimmering: true });
                // Stop shimmer after animation completes
                setTimeout(() => {
                    this.setState({ isShimmering: false });
                }, 1500);
            }
        }, 10000);
    }

    stopShimmerInterval() {
        if (this.shimmerInterval) {
            clearInterval(this.shimmerInterval);
            this.shimmerInterval = null;
        }
        this.setState({ isShimmering: false });
    }

    componentDidMount() {
        this.startShimmerInterval();
        this.trackGuestPromptOpen(false, this.isShown(this.props, this.state));
    }

    // Same condition render() uses to show the prompt
    isShown(props, state) {
        return state.isVisible && (props.limited || state.initialGuestShow || props.cta != null || state.hasReceivedLimitation);
    }

    // guest_limit_shown once per open of the guest prompt: lab_load (on-load prompt) or locked_feature
    trackGuestPromptOpen(wasShown, isShown) {
        const source = guestPromptOpenSource({ user: this.props.user, wasShown, isShown, limitedRose: this.lockedFeaturePending === true });
        if (isShown) this.lockedFeaturePending = false;
        if (!source) return;
        // On mobile the prompt is the single action button; the engine never hard-limits mobile, so only lab_load occurs
        captureEvent('guest_limit_shown', { source });
        if (typeof window !== 'undefined') trackGuestLimitShown(window.gtag, source);
    }

    componentDidUpdate(prevProps, prevState) {
        // A locked feature raised `limited`; the open it causes may land in this update or the next (isVisible)
        if (this.props.limited && !prevProps.limited) this.lockedFeaturePending = true;
        this.trackGuestPromptOpen(this.isShown(prevProps, prevState), this.isShown(this.props, this.state));

        // Reset visibility when limited prop becomes true (reopening the message)
        if (this.props.limited && !prevProps.limited && !this.state.isVisible) {
            this.setState({ isVisible: true });
        }

        // Trigger shake and rainbow animation when limited becomes true
        if (this.props.limited && !prevProps.limited) {
            this.setState({ isShaking: true, hasReceivedLimitation: true });
            this.handleLockedFeatureTrigger();
            if (this.props.cta === null) {
                this.startRainbowAnimation();
            }
            // Stop after animation duration (1s shake + 0.8s extra rainbow)
            setTimeout(() => {
                this.setState({ isShaking: false });
                if (this.props.cta === null) {
                    this.stopRainbowAnimation();
                }
            }, 1800);
        }

        // If user signs in on another tab and returns, the user prop changes from null to a user object.
        // We should clear the initial guest show state.
        if (this.props.user?.id !== prevProps.user?.id) {
            this.setState({ triggerCount: this.readTriggerCount() });
        }
        if (this.props.user !== prevProps.user && this.props.user != null) {
            if (this.state.initialGuestShow) {
                this.setState({ initialGuestShow: false, isVisible: false }); // Also set isVisible to false to hide it immediately if limited is not true
            }
        }
    }

    componentWillUnmount() {
        this.stopRainbowAnimation();
        this.stopShimmerInterval();
    }

    render() {
        const { cta, user, l } = this.props;
        const messageBody = cta?.message?.body;
        const segmentWidth = isMobile ? 350 : 450;
        // On mobile, use margin auto for centering instead of transform
        const segmentLeft = isMobile ? 0 : 21;
        const segmentRight = isMobile ? 0 : 'auto';
        const segmentMargin = isMobile ? '0 auto' : '0';
        const marginLeft = isMobile ? undefined : 150;

        // Localization data for all supported languages
        const translations = {
            en: {
                upgradeExperience: 'Upgrade Your Experience',
                premiumBenefits: 'Benefits',
                unlimitedLabTime: 'Unlimited Lab Time',
                noTimeRestrictions: 'No time restrictions',
                saveUnlimitedCircuits: 'Save your experiments',
                neverLoseWork: 'Never lose your work',
                allComponents: '40+ Components',
                fullComponentLibrary: 'Real-looking parts',
                prioritySupport: 'Priority Support',
                helpWhenNeeded: 'Help when you need it',
                testimonial: 'It\'s enjoyable to interact with. The user interface gives me the sensation of operating a real Relay unit in a laboratory.',
                testimonialAuthor: 'Hobbyist in Queensland',
                continueWithLimited: 'Continue with Limited Access',
                upgradeNow: 'Start with {cheapest_plan}',
                startNowForOnly: 'Start now for only {price}',
                schoolLicenseMessage: 'We kindly ask students to check with their teachers about obtaining a school license to continue using DCACLab.',
                signUpForFreeTrial: 'Sign up for a free trial',
                signUpNow: 'Sign up now',
                noCreditCardRequired: 'No credit card required',
                tryFree: 'Try DCACLab Free',
                requestClassroomLicense: 'Request classroom license',
                startFreeClassroom: 'Start a free classroom',
                contactTeamAccess: 'Contact us for team access',
                askTeacherSecondary: 'Or ask your teacher to get DCACLab for your class',
                seePlans: 'See plans'
            },
            ar: {
                upgradeExperience: 'ترقية تجربتك',
                premiumBenefits: 'مزايا',
                unlimitedLabTime: 'وقت مختبر غير محدود',
                noTimeRestrictions: 'لا قيود زمنية',
                saveUnlimitedCircuits: 'احفظ تجاربك',
                neverLoseWork: 'لن تفقد عملك أبدًا',
                allComponents: '40+ مكونات',
                fullComponentLibrary: 'قطع ذات مظهر حقيقي',
                prioritySupport: 'دعم ذو أولوية',
                helpWhenNeeded: 'المساعدة عند الحاجة',
                testimonial: 'من الممتع التفاعل معه. واجهة المستخدم تعطيني إحساس بتشغيل وحدة مرحل حقيقية في المختبر.',
                testimonialAuthor: 'هاوٍ في كوينزلاند',
                continueWithLimited: 'المتابعة بوصول محدود',
                upgradeNow: 'ابدأ مع {cheapest_plan}',
                startNowForOnly: 'ابدأ الآن مقابل {price} فقط',
                schoolLicenseMessage: 'نطلب بلطف من الطلاب التحقق مع معلميهم بشأن الحصول على ترخيص مدرسي لمواصلة استخدام DCACLab.',
                signUpForFreeTrial: 'سجل للحصول على تجربة مجانية',
                signUpNow: 'سجل الان',
                noCreditCardRequired: 'لا حاجة لبطاقة ائتمان',
                tryFree: 'جرّب DCACLab مجانًا',
                requestClassroomLicense: 'اطلب ترخيص الفصل الدراسي',
                startFreeClassroom: 'ابدأ فصلاً دراسياً مجانياً',
                contactTeamAccess: 'تواصل معنا للحصول على وصول الفريق',
                askTeacherSecondary: 'أو اطلب من معلمك توفير DCACLab لفصلك',
                seePlans: 'عرض الخطط'
            },
            de: {
                upgradeExperience: 'Verbessern Sie Ihr Erlebnis',
                premiumBenefits: 'Vorteile',
                unlimitedLabTime: 'Unbegrenzte Laborzeit',
                noTimeRestrictions: 'Keine Zeitbeschränkungen',
                saveUnlimitedCircuits: 'Speichern Sie Ihre Experimente',
                neverLoseWork: 'Verlieren Sie nie Ihre Arbeit',
                allComponents: '40+ Komponenten',
                fullComponentLibrary: 'Realistisch aussehende Teile',
                prioritySupport: 'Prioritäts-Support',
                helpWhenNeeded: 'Hilfe, wenn Sie sie brauchen',
                testimonial: 'Es macht Spaß, damit zu interagieren. Die Benutzeroberfläche gibt mir das Gefühl, ein echtes Relais in einem Labor zu bedienen.',
                testimonialAuthor: 'Hobbyist in Queensland',
                continueWithLimited: 'Mit eingeschränktem Zugriff fortfahren',
                upgradeNow: 'Starten Sie mit {cheapest_plan}',
                startNowForOnly: 'Jetzt starten für nur {price}',
                schoolLicenseMessage: 'Wir bitten Schüler höflich, mit ihren Lehrern über den Erwerb einer Schullizenz zu sprechen, um DCACLab weiterhin nutzen zu können.',
                signUpForFreeTrial: 'Melden Sie sich für eine kostenlose Testversion an',
                signUpNow: 'Jetzt registrieren',
                noCreditCardRequired: 'Keine Kreditkarte benötigt',
                tryFree: 'DCACLab kostenlos testen',
                requestClassroomLicense: 'Klassenlizenz anfragen',
                startFreeClassroom: 'Kostenlose Klasse starten',
                contactTeamAccess: 'Kontaktieren Sie uns für Teamzugang',
                askTeacherSecondary: 'Oder bitten Sie Ihre Lehrkraft, DCACLab für Ihre Klasse zu holen',
                seePlans: 'Tarife ansehen'
            },
            fr: {
                upgradeExperience: 'Améliorez Votre Expérience',
                premiumBenefits: 'Avantages',
                unlimitedLabTime: 'Temps de Laboratoire Illimité',
                noTimeRestrictions: 'Pas de restrictions de temps',
                saveUnlimitedCircuits: 'Sauvegardez vos expériences',
                neverLoseWork: 'Ne perdez jamais votre travail',
                allComponents: '40+ Composants',
                fullComponentLibrary: 'Pièces d\'apparence réaliste',
                prioritySupport: 'Support Prioritaire',
                helpWhenNeeded: 'De l\'aide quand vous en avez besoin',
                testimonial: 'C\'est agréable d\'interagir avec. L\'interface utilisateur me donne la sensation de faire fonctionner une unité de relais réelle dans un laboratoire.',
                testimonialAuthor: 'Passionné au Queensland',
                continueWithLimited: 'Continuer avec un Accès Limité',
                upgradeNow: 'Commencez avec {cheapest_plan}',
                startNowForOnly: 'Commencez maintenant pour seulement {price}',
                schoolLicenseMessage: 'Nous demandons gentiment aux étudiants de se renseigner auprès de leurs enseignants concernant l\'obtention d\'une licence scolaire pour continuer à utiliser DCACLab.',
                signUpForFreeTrial: 'Inscrivez-vous pour un essai gratuit',
                signUpNow: 'S\'inscrire maintenant',
                noCreditCardRequired: 'Aucune carte de crédit requise',
                tryFree: 'Essayez DCACLab gratuitement',
                requestClassroomLicense: 'Demandez une licence classe',
                startFreeClassroom: 'Créez une classe gratuite',
                contactTeamAccess: 'Contactez-nous pour un accès équipe',
                askTeacherSecondary: 'Ou demandez à votre professeur d\'obtenir DCACLab pour votre classe',
                seePlans: 'Voir les offres'
            },
            es: {
                upgradeExperience: 'Mejora Tu Experiencia',
                premiumBenefits: 'Beneficios',
                unlimitedLabTime: 'Tiempo de Laboratorio Ilimitado',
                noTimeRestrictions: 'Sin restricciones de tiempo',
                saveUnlimitedCircuits: 'Guarda tus experimentos',
                neverLoseWork: 'Nunca pierdas tu trabajo',
                allComponents: '40+ Componentes',
                fullComponentLibrary: 'Partes de aspecto real',
                prioritySupport: 'Soporte Prioritario',
                helpWhenNeeded: 'Ayuda cuando la necesites',
                testimonial: 'Es agradable interactuar con él. La interfaz de usuario me da la sensación de operar una unidad de relé real en un laboratorio.',
                testimonialAuthor: 'Aficionado en Queensland',
                continueWithLimited: 'Continuar con Acceso Limitado',
                upgradeNow: '¡Empezar con {cheapest_plan}',
                startNowForOnly: '¡Empieza ahora por solo {price}!',
                schoolLicenseMessage: 'Para seguir usando DCACLab, los estudiantes necesitan una licencia escolar. Por favor, pide a tu profesor que compre una para acceso ilimitado.',
                signUpForFreeTrial: 'Regístrese para una prueba gratuita',
                signUpNow: 'Regístrate ahora',
                noCreditCardRequired: 'No se requiere tarjeta de crédito',
                tryFree: 'Prueba DCACLab gratis',
                requestClassroomLicense: 'Solicita licencia para el aula',
                startFreeClassroom: 'Crea un aula gratis',
                contactTeamAccess: 'Contáctanos para acceso de equipo',
                askTeacherSecondary: 'O pídele a tu profesor que consiga DCACLab para tu clase',
                seePlans: 'Ver planes'
            },
            tr: {
                upgradeExperience: 'Deneyiminizi Yükseltin',
                premiumBenefits: 'Avantajlar',
                unlimitedLabTime: 'Sınırsız Laboratuvar Süresi',
                noTimeRestrictions: 'Zaman kısıtlaması yok',
                saveUnlimitedCircuits: 'Deneyimlerinizi kaydedin',
                neverLoseWork: 'Çalışmalarınızı asla kaybetmeyin',
                allComponents: '40+ Bileşen',
                fullComponentLibrary: 'Gerçek görünümlü parçalar',
                prioritySupport: 'Öncelikli Destek',
                helpWhenNeeded: 'İhtiyacınız olduğunda yardım',
                testimonial: 'Onunla etkileşim kurmak keyifli. Kullanıcı arabirimi bana laboratuvarda gerçek bir röle birimi çalıştırdığım hissini veriyor.',
                testimonialAuthor: 'Queensland\'deki Hobi Meraklısı',
                continueWithLimited: 'Sınırlı Erişimle Devam Et',
                upgradeNow: '{cheapest_plan} ile başlayın!',
                startNowForOnly: 'Şimdi sadece {price} ile başlayın',
                schoolLicenseMessage: 'Öğrencilerden DCACLab\'u kullanmaya devam etmek için öğretmenleriyle okul lisansı alma konusunda görüşmelerini nazikce rica ediyoruz.',
                signUpForFreeTrial: 'Ücretsiz deneme için kaydolun',
                signUpNow: 'Şimdi üye Ol',
                noCreditCardRequired: 'Kredi kartı gerekmez',
                tryFree: 'DCACLab\'ı ücretsiz deneyin',
                requestClassroomLicense: 'Sınıf lisansı isteyin',
                startFreeClassroom: 'Ücretsiz sınıf başlatın',
                contactTeamAccess: 'Ekip erişimi için bize ulaşın',
                askTeacherSecondary: 'Ya da öğretmeninizden sınıfınız için DCACLab almasını isteyin',
                seePlans: 'Planları görün'
            },
            th: {
                upgradeExperience: 'อัปเกรดประสบการณ์ของคุณ',
                premiumBenefits: 'สิทธิประโยชน์',
                unlimitedLabTime: 'เวลาห้องแล็บไม่จำกัด',
                noTimeRestrictions: 'ไม่มีข้อจำกัดด้านเวลา',
                saveUnlimitedCircuits: 'บันทึกการทดลองของคุณ',
                neverLoseWork: 'ไม่สูญเสียงานของคุณ',
                allComponents: '40+ ส่วนประกอบ',
                fullComponentLibrary: 'ชิ้นส่วนที่ดูจริง',
                prioritySupport: 'การสนับสนุนที่มีความสำคัญ',
                helpWhenNeeded: 'ช่วยเหลือเมื่อคุณต้องการ',
                testimonial: 'มันสนุกที่จะโต้ตอบด้วย อินเทอร์เฟซให้ความรู้สึกเหมือนการใช้งานหน่วยรีเลย์จริงในห้องแล็บ',
                testimonialAuthor: 'นักสะสมงานอดิเรกในควีนส์แลนด์',
                continueWithLimited: 'ดำเนินการต่อด้วยการเข้าถึงแบบจำกัด',
                upgradeNow: 'เริ่มต้นด้วย {cheapest_plan}',
                startNowForOnly: 'เริ่มต้นเลยเพียง {price}',
                schoolLicenseMessage: 'เราขอเรียนเชิญให้นักเรียนตรวจสอบกับครูผู้สอนของพวกเขาเกี่ยวกับการขอรับใบอนุญาตสำหรับโรงเรียนเพื่อใช้ DCACLab ต่อไป',
                signUpForFreeTrial: 'สมัครใช้งานเพื่อทดลองใช้ฟรี',
                signUpNow: 'สมัครออนไลน์ตอนนี้',
                noCreditCardRequired: 'ไม่ต้องใช้บัตรเครดิต',
                tryFree: 'ลองใช้ DCACLab ฟรี',
                requestClassroomLicense: 'ขอใบอนุญาตห้องเรียน',
                startFreeClassroom: 'เริ่มห้องเรียนฟรี',
                contactTeamAccess: 'ติดต่อเราสำหรับการเข้าถึงทีม',
                askTeacherSecondary: 'หรือขอให้ครูของคุณจัดหา DCACLab สำหรับห้องเรียนของคุณ',
                seePlans: 'ดูแผนราคา'
            }
        };

        // The lab serves Mexico as its own locale with the Spanish copy
        translations.mx = { ...translations.es };

        // Get the correct translations based on locale, falling back to English key by key
        const t = { ...translations['en'], ...(translations[this.props.current_locale] || {}) };
        // getCtaForUser runs outside render(), where `translations` does not exist; carry the flag on t
        t.__hasInlineCopy = !!translations[this.props.current_locale];
        const ctaInfo = this.getCtaForUser(t);

        const isVisibleCheck = this.isShown(this.props, this.state);

        // Mobile: show only the action button without modal
        if (isMobile && isVisibleCheck) {
            return (
                <div
                    className={this.state.isShaking ? 'mobile-shaking' : ''}
                    style={{
                        position: 'fixed',
                        bottom: 30,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center'
                    }}>
                    <Button
                        id="action-message-button"
                        className={`${(this.state.isShaking && this.props.cta === null) ? "rainbow-button" : ""} ${this.state.isShimmering ? "shimmer-effect" : ""}`.trim()}
                        style={(this.state.isShaking && this.props.cta === null) ? {
                            fontSize: '0.9rem',
                            backgroundColor: this.rainbowColors[this.state.rainbowFrame],
                            color: '#ffffff',
                            textShadow: '1px 1px 2px rgba(0,0,0,0.8), -1px -1px 2px rgba(0,0,0,0.8), 0px 0px 3px rgba(0,0,0,0.9)',
                            fontWeight: 'bold',
                            border: '2px solid rgba(255,255,255,0.8)',
                            boxShadow: '0 0 15px rgba(255,255,255,0.6), inset 0 0 10px rgba(255,255,255,0.3)',
                            transition: 'all 0.05s ease',
                            minHeight: '42px',
                            height: '42px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '10px 15px'
                        } : {
                            fontSize: '0.9rem',
                            backgroundColor: '#75b813',
                            color: '#ffffff',
                            minHeight: '42px',
                            height: '42px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '10px 15px'
                        }}
                        onClick={() => this.handleCtaClick(t)}
                        content={ctaInfo.text}
                    />
                    {ctaInfo.subtext && (
                        <div style={{ color: 'var(--card-foreground)', fontSize: '0.8rem', marginTop: '5px', textShadow: '0px 0px 2px var(--card)', fontWeight: 'bold' }}>
                            {ctaInfo.subtext}
                        </div>
                    )}
                    {this.renderSecondaryCta(ctaInfo, t, true)}
                </div>
            );
        }

        return isVisibleCheck ? (
                <Segment
                    className={this.state.isShaking && !isMobile ? "desktop-shaking" : ""}
                    style={{
                        padding: 9,
                        margin: segmentMargin,
                        ...(marginLeft !== undefined && { marginLeft }),
                        color: 'var(--card-foreground)',
                        background: 'var(--card)',
                        border: '2px solid var(--border)',
                        borderRadius: 'var(--radius)',
                        boxShadow: 'var(--shadow-xl)',
                        width: segmentWidth,
                        left: segmentLeft,
                        right: segmentRight,
                        position: 'fixed',
                        bottom: 33,
                        zIndex: 1000,
                        overflowX: 'hidden', // Hide horizontal scroll
                        overflowY: 'auto', // Keep vertical scroll if needed
                        maxHeight: 'calc(100vh - 20px)', // Maximum height relative to the viewport, with some space
                        maxWidth: 'calc(100vw - 40px)', // Prevent exceeding viewport width
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        boxSizing: 'border-box', // Include padding and border in width calculation
                    }}
                >
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                        <Header style={{
                            marginBottom: 0,
                            padding: '0 40px',
                            width: '100%',
                            color: 'var(--card-foreground)',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none'
                        }} textAlign='center'>{this.props.user ? (this.props.cta?.title || l.demo_mode) : (l.sign_up_for_free_trial || 'Sign up for a free trial')}</Header>
                        <Button
                            icon
                            className="action-message-close-button"
                            onClick={() => {
                                this.setState({ isVisible: false, initialGuestShow: false });
                                this.props.setLimitation(false);
                                this.props.setLimited(false);
                            }
                            }
                            style={{
                                position: 'absolute',
                                right: '-8px',
                                top: '-8px',
                                padding: 0,
                                margin: 0,
                                width: '28px',
                                height: '28px',
                                minHeight: '28px',
                                minWidth: '28px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Icon
                                name='remove'
                                style={{
                                    margin: 0,
                                    padding: 0,
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '100%'
                                }}
                            />
                        </Button>
                        <hr style={{ width: '100%', borderTop: '1px solid var(--border)', marginBottom: 15 }} /> {/* Separator line */}
                        <div style={{
                            fontSize: 'initial',
                            paddingBottom: 15,
                            textAlign: 'center',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none'
                        }}>
                            {this.props.user ? (messageBody ?
                                <div dangerouslySetInnerHTML={{ __html: messageBody }} />
                                : <p></p>) : <p style={{opacity: 0.8}}>{l.demo_mode}</p>
                            }
                        </div>
                    </div>


                    <div style={{
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        isolation: 'isolate'
                    }}>
                        <Button
                            id="action-message-button"
                            className={`${(this.state.isShaking && this.props.cta === null) ? "rainbow-button" : ""} ${this.state.isShimmering ? "shimmer-effect" : ""}`.trim()}
                            style={(this.state.isShaking && this.props.cta === null) ? {
                                fontSize: 'initial',
                                backgroundColor: this.rainbowColors[this.state.rainbowFrame],
                                color: '#ffffff',
                                textShadow: '1px 1px 2px rgba(0,0,0,0.8), -1px -1px 2px rgba(0,0,0,0.8), 0px 0px 3px rgba(0,0,0,0.9)',
                                fontWeight: 'bold',
                                border: '2px solid rgba(255,255,255,0.8)',
                                boxShadow: '0 0 15px rgba(255,255,255,0.6), inset 0 0 10px rgba(255,255,255,0.3)',
                                transition: 'all 0.05s ease',
                                minHeight: '36px',
                                height: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '8px 12px'
                            } : {
                                fontSize: 'initial',
                                backgroundColor: 'var(--primary)',
                                color: 'var(--primary-foreground)',
                                border: '1px solid var(--primary-foreground)',
                                minHeight: '36px',
                                height: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '8px 12px'
                            }}
                            onClick={() => this.handleCtaClick(t)}
                            content={ctaInfo.text}
                        />
                        {ctaInfo.subtext && (
                            <div style={{ fontSize: '0.8rem', marginTop: '6px', opacity: 0.8, textAlign: 'center' }}>
                                {ctaInfo.subtext}
                            </div>
                        )}
                        {this.renderSecondaryCta(ctaInfo, t, false)}
                    </div>
                    {this.state.isShaking && <div style={{ display: 'none' }}>RAINBOW ACTIVE - Frame: {this.state.rainbowFrame} - Color: {this.rainbowColors[this.state.rainbowFrame]}</div>}
                </Segment>
        ) : <div></div>;
    }
}
